import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserPlus, ChevronDown } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { usePerformanceLab, LabPlayer, PlayerStatus } from "@/contexts/PerformanceLabContext";
import { analytics } from "@/lib/analytics";
import { cn } from "@/lib/utils";

const STATUS_COLOURS: Record<PlayerStatus, string> = {
  fit: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  injured: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  suspended: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  unavailable: "bg-muted text-muted-foreground",
};

function PlayerRow({ player }: { player: LabPlayer }) {
  const { translate } = useTranslation();
  const { updatePlayerStatus } = usePerformanceLab();
  const [open, setOpen] = useState(false);

  const name = player.is_hypothetical
    ? player.first_name
    : `${player.first_name} ${player.last_name}`;

  return (
    <div className="flex items-center gap-3 py-2 px-3 hover:bg-muted/40 rounded-md transition-colors">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate flex items-center gap-2">
          {name}
          {player.is_hypothetical && (
            <Badge variant="outline" className="text-[10px] border-dashed shrink-0">
              {translate("lab_hypothetical_badge")}
            </Badge>
          )}
        </p>
        <p className="text-xs text-muted-foreground">{player.position ?? "—"}</p>
        {player.rtp_stage && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            {translate("lab_rtp_stage")}: {player.rtp_stage}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => setOpen(!open)}
          className={cn(
            "flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border",
            STATUS_COLOURS[player.status],
          )}
        >
          {translate(`lab_status_${player.status}`)}
          <ChevronDown className="h-3 w-3" />
        </button>
      </div>

      {open && (
        <div className="absolute z-10 mt-8 bg-card border rounded-md shadow-lg p-1 min-w-[140px]">
          {(["fit", "injured", "suspended", "unavailable"] as PlayerStatus[]).map((s) => (
            <button
              key={s}
              onClick={() => {
                updatePlayerStatus(player.id, s);
                analytics.track("lab.squad_state_changed", { change_type: "status" });
                setOpen(false);
              }}
              className={cn(
                "w-full text-left px-3 py-1.5 text-xs rounded hover:bg-muted transition-colors",
                player.status === s && "font-semibold",
              )}
            >
              {translate(`lab_status_${s}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AddHypotheticalDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { translate } = useTranslation();
  const { addHypotheticalPlayer } = usePerformanceLab();
  const [position, setPosition] = useState("");
  const [label, setLabel] = useState("");

  const handleAdd = () => {
    if (!position || !label) return;
    addHypotheticalPlayer(position, label);
    analytics.track("lab.squad_state_changed", { change_type: "hypothetical_added" });
    setPosition("");
    setLabel("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm z-[120]">
        <DialogHeader>
          <DialogTitle>{translate("lab_add_hypothetical_title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label>{translate("lab_add_hypothetical_position")}</Label>
            <Select value={position} onValueChange={setPosition}>
              <SelectTrigger>
                <SelectValue placeholder={translate("lab_add_hypothetical_position_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                {["GK", "CB", "LB", "RB", "CM", "DM", "AM", "LW", "RW", "ST"].map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{translate("lab_add_hypothetical_label")}</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={translate("lab_add_hypothetical_label_placeholder")}
            />
          </div>
          <Button onClick={handleAdd} disabled={!position || !label} className="w-full">
            {translate("lab_add_hypothetical_btn")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function LabSquadPanel() {
  const { translate } = useTranslation();
  const { session } = usePerformanceLab();
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const groups: Record<PlayerStatus, LabPlayer[]> = {
    fit: session.players.filter((p) => p.status === "fit"),
    injured: session.players.filter((p) => p.status === "injured"),
    suspended: session.players.filter((p) => p.status === "suspended"),
    unavailable: session.players.filter((p) => p.status === "unavailable"),
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
        <h3 className="font-semibold text-sm">{translate("lab_squad_panel_title")}</h3>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5 h-7 text-xs"
          onClick={() => setAddDialogOpen(true)}
        >
          <UserPlus className="h-3.5 w-3.5" />
          {translate("lab_add_hypothetical_btn_short")}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-4">
        {(["fit", "injured", "suspended", "unavailable"] as PlayerStatus[]).map((status) => {
          const players = groups[status];
          if (players.length === 0) return null;
          return (
            <div key={status}>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-3 mb-1">
                {translate(`lab_status_${status}`)} ({players.length})
              </p>
              <div className="relative space-y-0.5">
                {players.map((p) => (
                  <PlayerRow key={p.id} player={p} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <AddHypotheticalDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />
    </div>
  );
}
